import { Field, TextInput, TextArea, SelectInput } from "@/components/setup/SetupField";
import { useLang } from "@/i18n";

const SEGMENTS = [
  "Cafés & Bars",
  "Restaurants",
  "Hotels & Resorts",
  "QSR / Fast Food",
  "Nightlife & Clubs",
  "Bakeries & Pastry",
  "Other",
];

const GOLIVE_OPTIONS = ["Q3 2026", "Q4 2026", "Q1 2027", "Q2 2027", "Flexible / TBD"];

const AGENCY_OPTIONS = [
  {
    value: "master_fb",
    title: "Master F&B manages printing",
    desc: "Master F&B selects and manages the printing agency end-to-end (QR cube stickers, signage).",
  },
  {
    value: "own_agency",
    title: "We use our own local agency",
    desc: "Your BU works with its preferred local agency — Master F&B provides print-ready artwork, technical specs and tooling.",
  },
];

export const StepQrMenu = ({ form, update, errors }) => {
  const { t } = useLang();
  const toggleSegment = (seg) => {
    const has = form.segments.includes(seg);
    update({ segments: has ? form.segments.filter((s) => s !== seg) : [...form.segments, seg] });
  };

  return (
    <div className="space-y-6" data-testid="setup-step-qrmenu">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field
          label="Estimated number of outlets"
          required
          error={errors.outlets_estimate}
          hint="Rough estimate for the first wave is fine"
          testId="setup-field-outlets"
        >
          <TextInput
            value={form.outlets_estimate}
            error={errors.outlets_estimate}
            onChange={(e) => update({ outlets_estimate: e.target.value })}
            placeholder="e.g. 500"
            data-testid="setup-input-outlets"
          />
        </Field>
        <Field label="Target go-live" testId="setup-field-golive">
          <SelectInput
            options={GOLIVE_OPTIONS}
            placeholder="Select timeframe…"
            value={form.golive_target}
            onChange={(e) => update({ golive_target: e.target.value })}
            data-testid="setup-input-golive"
          />
        </Field>
      </div>

      <Field label="Target client segments" testId="setup-field-segments">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
          {SEGMENTS.map((seg) => {
            const active = form.segments.includes(seg);
            return (
              <button
                key={seg}
                type="button"
                onClick={() => toggleSegment(seg)}
                className={`flex items-center gap-2.5 border rounded-sm px-3.5 py-2.5 text-left text-sm transition-colors ${
                  active
                    ? "border-[#dd260e] bg-[#dd260e]/5 text-[#000000] font-semibold"
                    : "border-[#e8e8e8] text-[#555555] hover:border-[#000000]"
                }`}
                data-testid={`setup-segment-${seg.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <span
                  className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
                    active ? "bg-[#dd260e] border-[#dd260e]" : "border-[#cccccc]"
                  }`}
                >
                  {active && <span className="text-white text-[10px] leading-none">✓</span>}
                </span>
                {t(seg)}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Print agency for physical assets" required error={errors.print_agency} testId="setup-field-print-agency">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          {AGENCY_OPTIONS.map((opt) => {
            const active = form.print_agency === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update({ print_agency: opt.value })}
                className={`border rounded-sm p-4 text-left transition-colors ${
                  active ? "border-[#dd260e] bg-[#dd260e]/5" : "border-[#e8e8e8] hover:border-[#000000]"
                }`}
                data-testid={`setup-agency-${opt.value.replace(/_/g, "-")}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      active ? "border-[#dd260e]" : "border-[#cccccc]"
                    }`}
                  >
                    {active && <span className="w-2 h-2 rounded-full bg-[#dd260e]" />}
                  </span>
                  <span className="text-sm font-bold text-[#000000]">{t(opt.title)}</span>
                </div>
                <p className="text-xs text-[#555555] leading-relaxed pl-6">{t(opt.desc)}</p>
              </button>
            );
          })}
        </div>
      </Field>

      {form.print_agency === "own_agency" && (
        <Field
          label="Print contact / agency"
          required
          error={errors.print_contact}
          hint="Where Master F&B sends print-ready files — contact email or the name of your printing agency"
          testId="setup-field-print-contact"
        >
          <TextInput
            value={form.print_contact}
            error={errors.print_contact}
            onChange={(e) => update({ print_contact: e.target.value })}
            placeholder="e.g. maxim.sendrea@cchellenic.com or 'PrintCo Chisinau'"
            data-testid="setup-input-print-contact"
          />
        </Field>
      )}

      <Field
        label="Additional requirements"
        hint="Optional — allergen rules, local legal requirements, special menu structures…"
        testId="setup-field-requirements"
      >
        <TextArea
          value={form.additional_requirements}
          onChange={(e) => update({ additional_requirements: e.target.value })}
          placeholder="Optional"
          data-testid="setup-input-requirements"
        />
      </Field>
    </div>
  );
};
